/**
 * Who may use Madaline — the rules the Terms of service state under "Who can
 * use Madaline", as data the API enforces and the screens quote.
 *
 * One file for both, so the sentence a person ticks, the definition beside a
 * checkbox and the list the server refuses against cannot drift apart:
 * `packages/shared/test/legal-eligibility.test.ts` asserts the Terms carry the
 * same definition and the same places.
 *
 * BROWSER-SAFE: no Node imports.
 */

// ---------------------------------------------------------------------------
// Sanctions
// ---------------------------------------------------------------------------

/**
 * Countries under comprehensive United States sanctions, as the Terms name
 * them ("Sanctions"). ISO 3166-1 alpha-2.
 *
 * Primary authorities, read 2026-10-15: Cuba — the Cuban Assets Control
 * Regulations, 31 CFR Part 515; Iran — the Iranian Transactions and Sanctions
 * Regulations, 31 CFR Part 560; North Korea — the North Korea Sanctions
 * Regulations, 31 CFR Part 510. Syria is NOT here: Executive Order 14312
 * (June 30, 2025) ended the countrywide Syria program from July 1, 2025, and
 * OFAC removed 31 CFR Part 542 on August 26, 2025; what remains is list-based.
 *
 * The Terms hold this list open for counsel (the counsel decision marker under
 * "Sanctions"). Change it here and there together.
 */
export const SANCTIONED_COUNTRIES = ["CU", "IR", "KP"] as const;

/**
 * Regions of Ukraine under comprehensive sanctions: Crimea (Executive Order
 * 13685, December 19, 2014) and the so-called Donetsk and Luhansk People's
 * Republic regions (Executive Order 14065, February 21, 2022).
 *
 * EO 14065 names the "so-called" republics, whose boundaries no official map
 * draws. A person cannot be asked to say which side of a front line they live
 * on, so Madaline refuses the WHOLE of the Donetsk and Luhansk oblasts — a
 * conservative reading, set out for counsel in the launch report. The ISO
 * 3166-2 codes are UA-43 (Crimea), UA-40 (Sevastopol), UA-14 (Donetsk) and
 * UA-09 (Luhansk).
 */
export const SANCTIONED_UKRAINE_REGIONS = [
  { code: "UA-43", label: "Crimea" },
  { code: "UA-40", label: "Sevastopol" },
  { code: "UA-14", label: "Donetsk region" },
  { code: "UA-09", label: "Luhansk region" },
] as const;

/**
 * How each region is recognised when it is typed rather than chosen. `\b` is
 * an ASCII word boundary in JavaScript, so the Cyrillic and Crimean Tatar
 * spellings are matched as plain substrings.
 */
const UKRAINE_REGION_WORDS: ReadonlyArray<{ code: string; pattern: RegExp }> = [
  { code: "UA-43", pattern: /\b(?:crimea|crimean|krym|krim)\b|\bUA-?43\b|крим|крым|qırım/i },
  { code: "UA-40", pattern: /\b(?:sevastopol|sebastopol)\b|\bUA-?40\b|севастопол/i },
  { code: "UA-14", pattern: /\b(?:donetsk|donetska|donbass?|dnr|dpr)\b|\bUA-?14\b|донецьк|донецк/i },
  { code: "UA-09", pattern: /\b(?:luhansk|lugansk|luhanska|lnr|lpr)\b|\bUA-?09\b|луганськ|луганск/i },
];

const COUNTRY_NAMES: Readonly<Record<string, string>> = { CU: "Cuba", IR: "Iran", KP: "North Korea" };

export interface SanctionedPlace {
  /** ISO 3166-1 alpha-2 of the country. */
  readonly country: string;
  /** ISO 3166-2 of the region, when it is a region that is sanctioned. */
  readonly region: string | null;
  /** "Iran", "Crimea" — for the refusal a person reads. */
  readonly name: string;
}

export const isSanctionedCountry = (country: string | null | undefined): boolean =>
  typeof country === "string" && (SANCTIONED_COUNTRIES as readonly string[]).includes(country.toUpperCase());

/**
 * The sanctioned place a country and region name, or null.
 *
 * The region is matched whether it was CHOSEN (an ISO 3166-2 code) or TYPED
 * (free text, as the region field was until 2026-10-15): an answer of "Crimea"
 * typed into a box is the same answer.
 */
export function sanctionedPlace(country: string | null | undefined, region?: string | null): SanctionedPlace | null {
  if (typeof country !== "string") return null;
  const code = country.trim().toUpperCase();
  if (isSanctionedCountry(code)) return { country: code, region: null, name: COUNTRY_NAMES[code] ?? code };
  if (code === "UA" && typeof region === "string" && region.trim() !== "") {
    const hit = UKRAINE_REGION_WORDS.find((r) => r.pattern.test(region));
    if (hit) {
      const label = SANCTIONED_UKRAINE_REGIONS.find((r) => r.code === hit.code)?.label ?? hit.code;
      return { country: "UA", region: hit.code, name: label };
    }
  }
  return null;
}

/**
 * What a refused person reads: what happened, why, and what did not happen.
 * `outcome` is the last sentence — "No account was made." at sign-up. (The
 * refusal itself is kept in the audit log, so it never says nothing was kept.)
 */
export function sanctionsRefusal(
  place: SanctionedPlace | null,
  outcome = "Nothing about your account was changed.",
): string {
  const where = place === null ? "the place this connection comes from" : place.name;
  return (
    `Madaline is not available in ${where}. United States sanctions law, which Madaline follows, ` +
    `does not allow it to provide its service there. ${outcome}`
  );
}

// ---------------------------------------------------------------------------
// U.S. persons
// ---------------------------------------------------------------------------

/**
 * The Terms' definition ("U.S. persons"), verbatim but for its first words,
 * shown beside every "I am a U.S. person" question. The test asserts the
 * Terms contain it.
 */
export const US_PERSON_DEFINITION =
  "You are a U.S. person if you are a citizen or a permanent resident of the United States, " +
  "if you live in the United States, or if you are in the United States when you use Madaline.";

// ---------------------------------------------------------------------------
// The sign-up confirmation
// ---------------------------------------------------------------------------

/**
 * What a person confirms by ticking the box on the sign-up form. Recorded
 * verbatim with the account, beside the Terms and the Privacy policy it was
 * shown with. Changing these words changes what new accounts confirm, so
 * change them together with the Terms' "Eligibility" and "Sanctions", the
 * Privacy policy's "What you confirm when you create an account", and
 * `ELIGIBILITY_CLAIMS` below.
 *
 * The middle sentence since 2026-10-15: accounts are for individuals acting
 * for themselves (the owner's decision, counsel packet #2).
 */
export const SIGNUP_ELIGIBILITY_STATEMENT =
  "I am at least 18 years old and an adult where I live. I will use Madaline only for my own " +
  "trading, not for a company or for anyone else. I am not named on a U.S. sanctions list or " +
  "acting for anyone who is.";

/** The header the sign-up form sends it in, beside `x-madaline-agreements`. */
export const ELIGIBILITY_HEADER = "x-madaline-eligibility";

/**
 * The claims the header carries, one per rule the statement confirms, in this
 * order: `adult,individual,not_sanctioned`. The API requires every one, so a
 * form still showing the older two-rule sentence (`adult,not_sanctioned`) is
 * refused and reloads, instead of having this sentence recorded for it.
 */
export const ELIGIBILITY_CLAIMS = ["adult", "individual", "not_sanctioned"] as const;

export type EligibilityClaim = (typeof ELIGIBILITY_CLAIMS)[number];

export const ELIGIBILITY_HEADER_VALUE = ELIGIBILITY_CLAIMS.join(",");
